import type { HandwrittenNoteTypes } from '@mathflat/handwritten-note'
import type { DefaultResponse } from '@mathflat/shared/@types/mathflatApi'
import axios from 'axios'
import { toPng } from 'html-to-image'

import { datadogClient } from '~/@app/init/datadog'
import { errorHandlerService } from '~/@common/services'
import type { StudentWorksheetNoteValue } from '~/@common/services/indexedDb.service'

import maxios from '../utils/maxios'

export enum HandwrittenNoteType {
  STUDENT_WORKSHEET_SCORING = 'STUDENT_WORKSHEET_SCORING',
  STUDENT_EXAM_SCORING = 'STUDENT_EXAM_SCORING',
  SIGNATURE_WORKBOOK_PAGE = 'SIGNATURE_WORKBOOK_PAGE',
  ACADEMY_GROUP_WORKBOOK_PAGE = 'ACADEMY_GROUP_WORKBOOK_PAGE',
  STUDENT_WORKSHEET_SCORING_TEACHER = 'STUDENT_WORKSHEET_SCORING_TEACHER',
  ASSIGNED_CAS_SCORING = 'ASSIGNED_CAS_SCORING',
}

interface CasNoteParams {
  assignedId: number
  problemId: number
}

type NoteData = HandwrittenNoteTypes.NoteData

interface NoteParams {
  id: number
  subId: number
  type: HandwrittenNoteType
}

interface PresignedUrlResponse extends NoteParams {
  presignedUrl: string
}

interface HandwrittenNoteResponse extends NoteParams {
  handwrittenNoteUrl: string | null
}

interface SignatureWorkbookNoteParams {
  studentWorkbookRevisionId: number
  pageNumber: number
}

interface Api {
  fetchNote: (params: NoteParams) => Promise<NoteData | undefined>
  fetchNoteByUrl: (jsonUrl: string) => Promise<NoteData>
  fetchSignatureWorkbookNote: (params: SignatureWorkbookNoteParams) => Promise<NoteData | undefined>
  uploadSignatureWorkbookNote: (
    params: SignatureWorkbookNoteParams,
    data: NoteData,
  ) => Promise<void>
  fetchAcademyGroupWorkbookNote: (
    params: SignatureWorkbookNoteParams,
  ) => Promise<NoteData | undefined>
  uploadAcademyGroupWorkbookNote: (
    params: SignatureWorkbookNoteParams,
    data: NoteData,
  ) => Promise<void>
  uploadStudentWorksheetNote: (
    noteValue: StudentWorksheetNoteValue,
    noteType: HandwrittenNoteType,
  ) => Promise<void>
  uploadStudentWorksheetNotes: (
    noteValues: StudentWorksheetNoteValue[],
    noteType: HandwrittenNoteType,
  ) => Promise<void>
  uploadStudentWorksheetPng: (data: NoteParams) => Promise<string>
  fetchCasNote: (params: CasNoteParams) => Promise<NoteData | undefined>
  uploadCasNote: (params: CasNoteParams, data: NoteData) => Promise<void>
}

export const handwrittenNoteApi = ((): Api => {
  const fetchNote: Api['fetchNote'] = async ({ id, type, subId }) => {
    const { data } = await maxios.get<DefaultResponse<HandwrittenNoteResponse>>(
      `/handwritten-note/${id}/${type}`,
      {
        params: {
          subId,
        },
      },
    )
    if (data.handwrittenNoteUrl) {
      return await fetchNoteByUrl(data.handwrittenNoteUrl)
    }
  }

  const _fetchPresignedUrls = async (params: NoteParams[]) => {
    const { data } = await maxios.post<DefaultResponse<Array<PresignedUrlResponse>>>(
      '/handwritten-note/presigned-url',
      params,
    )
    return data
  }

  const _uploadNote = async (url: string, noteData: NoteData) => {
    return await maxios.put<void>(url, JSON.stringify(noteData), { withoutAuth: true })
  }

  // S3 업로드가 성공했음을 DB에 저장
  const _saveNoteUrl = async (params: NoteParams, uploadUrl: string) => {
    await maxios.post('/handwritten-note', {
      ...params,
      handwrittenNoteUrl: uploadUrl.split('?')[0],
    })
  }

  const fetchNoteByUrl: Api['fetchNoteByUrl'] = async (jsonUrl) => {
    // 필기 JSON 파일만 허용
    if (!jsonUrl.toLowerCase().endsWith('.json')) {
      throw new Error(`invalid json url: ${jsonUrl}`)
    }

    // jsonUrl을 그대로 요청하기 위해 maxios가 아니라 axios 사용함
    const { data } = await axios.get<NoteData>(`${jsonUrl}?timestamp=${Date.now()}`)
    return data
  }

  const fetchSignatureWorkbookNote: Api['fetchSignatureWorkbookNote'] = async (params) => {
    const noteParams = {
      id: params.studentWorkbookRevisionId,
      subId: params.pageNumber,
      type: HandwrittenNoteType.SIGNATURE_WORKBOOK_PAGE,
    }
    return await fetchNote(noteParams)
  }

  const uploadSignatureWorkbookNote: Api['uploadSignatureWorkbookNote'] = async (
    params,
    data: NoteData,
  ) => {
    const noteParams = {
      id: params.studentWorkbookRevisionId,
      subId: params.pageNumber,
      type: HandwrittenNoteType.SIGNATURE_WORKBOOK_PAGE,
    }
    const presignedUrlData = await _fetchPresignedUrls([noteParams])

    if (!presignedUrlData?.length) {
      throw new Error('no presignedUrlData')
    }

    const uploadUrl = presignedUrlData[0].presignedUrl

    await _uploadNote(uploadUrl, data)
    await _saveNoteUrl(noteParams, uploadUrl)
  }

  const fetchAcademyGroupWorkbookNote: Api['fetchAcademyGroupWorkbookNote'] = async (params) => {
    const noteParams = {
      id: params.studentWorkbookRevisionId,
      subId: params.pageNumber,
      type: HandwrittenNoteType.ACADEMY_GROUP_WORKBOOK_PAGE,
    }
    return await fetchNote(noteParams)
  }

  const uploadAcademyGroupWorkbookNote: Api['uploadAcademyGroupWorkbookNote'] = async (
    params,
    data: NoteData,
  ) => {
    const noteParams = {
      id: params.studentWorkbookRevisionId,
      subId: params.pageNumber,
      type: HandwrittenNoteType.ACADEMY_GROUP_WORKBOOK_PAGE,
    }
    const presignedUrlData = await _fetchPresignedUrls([noteParams])

    if (!presignedUrlData?.length) {
      throw new Error('no presignedUrlData')
    }

    const uploadUrl = presignedUrlData[0].presignedUrl

    await _uploadNote(uploadUrl, data)
    await _saveNoteUrl(noteParams, uploadUrl)
  }

  const uploadStudentWorksheetNote: Api['uploadStudentWorksheetNote'] = async (
    noteValue,
    noteType,
  ) => {
    if (
      noteType !== HandwrittenNoteType.STUDENT_WORKSHEET_SCORING &&
      noteType !== HandwrittenNoteType.STUDENT_EXAM_SCORING
    ) {
      throw new Error(`invalid noteType: ${noteType}`)
    }
    const noteParams = {
      id: noteValue.studentWorksheetId,
      subId: noteValue.worksheetProblemId,
      type: noteType,
    }

    const presignedUrlData = await _fetchPresignedUrls([noteParams])

    if (!presignedUrlData?.length) {
      throw new Error('no presignedUrlData')
    }

    const uploadUrl = presignedUrlData[0].presignedUrl

    await _uploadNote(uploadUrl, noteValue.noteData)
    await _saveNoteUrl(noteParams, uploadUrl)
  }

  const uploadStudentWorksheetNotes: Api['uploadStudentWorksheetNotes'] = async (
    noteValues,
    noteType,
  ) => {
    if (
      noteType !== HandwrittenNoteType.STUDENT_WORKSHEET_SCORING &&
      noteType !== HandwrittenNoteType.STUDENT_EXAM_SCORING
    ) {
      throw new Error(`invalid noteType: ${noteType}`)
    }

    const validNoteValues = noteValues.filter((item) => item.noteData.paths.length > 0)

    if (!validNoteValues.length) {
      return
    }

    interface UploadItem {
      uploadUrl: string
      type: HandwrittenNoteType
      noteValue?: StudentWorksheetNoteValue
      uploadTask?: Promise<void>
      isSuccess?: boolean
    }

    // 업로드 결과에 따라서 uploadItems 배열 내 객체의 isSuccess를 변경한 후 다시 return
    const uploadNoteItems = async (uploadItems: UploadItem[]) => {
      const validUploadItems = uploadItems.filter((item) =>
        Boolean(item.noteValue && item.uploadTask),
      )

      if (validUploadItems.length) {
        const result = await Promise.allSettled(validUploadItems.map((item) => item.uploadTask))

        result.forEach((item, index) => {
          // 업로드 성공하면 S3에서 200 응답, promsie가 reject되지 않으면 성공으로 간주
          if (item.status === 'fulfilled') {
            validUploadItems[index].isSuccess = true
          }
        })
      }
      return uploadItems
    }

    const getFailedUploadItems = (uploadItems: UploadItem[]) => {
      return uploadItems.filter((item) => !item.isSuccess)
    }

    const getValidNoteValueMapKey = (studentWorksheetId: number, worksheetProblemId: number) =>
      `${studentWorksheetId}:${worksheetProblemId}`

    const uploadUrlData = await _fetchPresignedUrls(
      validNoteValues.map(({ studentWorksheetId, worksheetProblemId }) => {
        return {
          id: studentWorksheetId,
          subId: worksheetProblemId,
          type: noteType,
        }
      }),
    )

    const validNoteValueMap: Map<string, StudentWorksheetNoteValue> = new Map(
      validNoteValues.map((noteValue) => [
        getValidNoteValueMapKey(noteValue.studentWorksheetId, noteValue.worksheetProblemId),
        noteValue,
      ]),
    )

    const uploadItems: UploadItem[] = uploadUrlData.map((item) => {
      const noteValue = validNoteValueMap.get(getValidNoteValueMapKey(item.id, item.subId))
      let uploadTask: Promise<void> | undefined

      if (noteValue) {
        uploadTask = _uploadNote(item.presignedUrl, noteValue.noteData)
      }

      return {
        uploadUrl: item.presignedUrl,
        type: item.type,
        noteValue,
        uploadTask,
      }
    })

    let uploadResult = await uploadNoteItems(uploadItems)
    let failedUploadItems = getFailedUploadItems(uploadResult)

    // 최초 업로드 실패하면 재시도
    if (failedUploadItems.length) {
      uploadResult = await uploadNoteItems(failedUploadItems)
    }

    const uploadedItems = uploadResult.filter((item) => item.isSuccess && item.noteValue)

    if (uploadedItems.length) {
      // 업로드 성공을 BE에 전송 (실패하더라도 S3 트리거에서 중복으로 처리함)
      const postUploadTasks = uploadedItems.map((item) => {
        if (!item.noteValue) {
          throw new Error('no noteValue')
        }
        const noteParams = {
          id: item.noteValue.studentWorksheetId,
          subId: item.noteValue.worksheetProblemId,
          type: item.type,
        }
        return _saveNoteUrl(noteParams, item.uploadUrl)
      })
      await Promise.allSettled(postUploadTasks)
    }

    failedUploadItems = getFailedUploadItems(uploadResult)

    if (failedUploadItems.length) {
      try {
        datadogClient.sendError(new Error('handwrittenNoteApi: failedUploadItems'), {
          context: {
            total: uploadItems.length,
            failed: failedUploadItems.length,
          },
          source: 'custom',
          attributes: {
            fingerprint: ['handwrittenNoteApi', 'failedUploadItems'],
          },
        })
      } catch (err) {
        // ignore error
      }
    }
  }

  const _fetchTempPresignedUrls = async (params: NoteParams[]) => {
    const { data } = await maxios.post<DefaultResponse<PresignedUrlResponse>>(
      '/handwritten-note/presigned-url/temp',
      params,
    )
    return data
  }

  const _uploadPng = async (url: string, file: File) => {
    return await maxios.put<void>(url, file, { withoutAuth: true })
  }

  const _dataURLtoBlob = (dataUrl: string) => {
    const arr = dataUrl.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
  }

  const _blobToFile = (blob: Blob, fileName: string) => {
    return new File([blob], fileName, { type: blob.type })
  }

  const _toPngAndUpload = async (url: string) => {
    const activeSwiper = document.querySelector('.swiper-slide-active')
    const scalableContent = activeSwiper?.querySelector(
      '[data-component="mn__ScalableContent"]',
    ) as HTMLElement

    const problemArea = activeSwiper?.querySelector('.problem-area') as HTMLElement

    try {
      const dataUrl = await toPng(scalableContent ?? problemArea, {
        cacheBust: true,
        skipFonts: true,
        pixelRatio: 0.5,
      })
      const blob = _dataURLtoBlob(dataUrl)
      const file = _blobToFile(blob, 'handWritten.png')
      return await _uploadPng(url, file)
    } catch (err) {
      errorHandlerService.handle(err)
    }
  }

  const uploadStudentWorksheetPng = async (data: NoteParams) => {
    const presignedUrlData = await _fetchTempPresignedUrls([data])

    if (presignedUrlData.presignedUrl.length < 1) {
      throw new Error('no presignedUrlData')
    }

    const uploadUrl = presignedUrlData.presignedUrl

    await _toPngAndUpload(uploadUrl)

    return uploadUrl.split('?')[0]
  }

  const fetchCasNote: Api['fetchCasNote'] = async ({ assignedId, problemId }) => {
    return await fetchNote({
      id: assignedId,
      subId: problemId,
      type: HandwrittenNoteType.ASSIGNED_CAS_SCORING,
    })
  }

  const uploadCasNote: Api['uploadCasNote'] = async ({ assignedId, problemId }, data) => {
    const noteParams = {
      id: assignedId,
      subId: problemId,
      type: HandwrittenNoteType.ASSIGNED_CAS_SCORING,
    }
    const presignedUrlData = await _fetchPresignedUrls([noteParams])

    if (!presignedUrlData?.length) {
      throw new Error('no presignedUrlData')
    }

    const uploadUrl = presignedUrlData[0].presignedUrl

    await _uploadNote(uploadUrl, data)
    await _saveNoteUrl(noteParams, uploadUrl)
  }

  return {
    fetchNote,
    fetchNoteByUrl,
    fetchSignatureWorkbookNote,
    uploadSignatureWorkbookNote,
    fetchAcademyGroupWorkbookNote,
    uploadAcademyGroupWorkbookNote,
    uploadStudentWorksheetNote,
    uploadStudentWorksheetNotes,
    uploadStudentWorksheetPng,
    fetchCasNote,
    uploadCasNote,
  }
})()
